import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOMeta from '@/components/base/SEOMeta';
import SchemaOrg from '@/components/base/SchemaOrg';
import { aboutInfo } from '@/mocks/about';

export default function About() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  return (
    <>
      <SEOMeta
        title="关于我们 - 屁屁影院 | 品牌介绍 口碑评价 联系方式"
        description="了解屁屁影院品牌故事、核心优势、用户评价和联系方式。屁屁影院是国内领先的免费在线影视资源平台，提供海量高清电影、电视剧、动漫在线观看服务。"
        keywords="屁屁影院,关于屁屁影院,屁屁影院品牌,屁屁影院联系方式,免费观影平台,屁屁影院评价"
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 屁屁影院',
          description: '了解屁屁影院品牌故事、核心优势、用户评价和联系方式',
          url: 'https://www.ppcinema.com/about',
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://www.ppcinema.com',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: '关于我们',
            },
          ],
        }}
      />
      <SchemaOrg
        type="FAQPage"
        data={{
          mainEntity: aboutInfo.faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        }}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="px-4 md:px-6 max-w-5xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mb-10 md:mb-14">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4 font-heading">
              关于 {aboutInfo.brandName}
            </h1>
            <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
              <p className="text-sm text-foreground-700 leading-relaxed mb-4">{aboutInfo.brandIntro}</p>
              <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-primary-50 border border-primary-200/50">
                <i className="ri-quote-text text-primary-500"></i>
                <p className="text-sm text-primary-800 font-medium">{aboutInfo.mission}</p>
              </div>
            </div>
          </section>

          <section className="mb-10 md:mb-14">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 font-heading">
              <i className="ri-star-line text-primary-500 mr-2"></i>
              数据一览
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {aboutInfo.stats.map((stat, i) => (
                <div
                  key={i}
                  className="text-center p-5 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading mb-1">
                    {stat.number}
                  </div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10 md:mb-14">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 font-heading">
              <i className="ri-heart-line text-accent-500 mr-2"></i>
              核心优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {aboutInfo.features.map((feature, i) => (
                <div
                  key={i}
                  className="p-5 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300/50 transition-colors"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 mb-3">
                    <i className={`${feature.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2 font-heading">{feature.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10 md:mb-14">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 font-heading">
              <i className="ri-chat-smile-2-line text-primary-500 mr-2"></i>
              用户评价
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {aboutInfo.reviews.map((review, i) => (
                <div
                  key={i}
                  className="p-5 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{review.name}</div>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: 5 }).map((_, j) => (
                          <i
                            key={j}
                            className={`text-xs ${j < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                          ></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                  <div className="mt-3 text-xs text-foreground-400">{review.date}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10 md:mb-14">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 font-heading">
              <i className="ri-question-answer-line text-accent-500 mr-2"></i>
              常见问题
            </h2>
            <div className="space-y-3">
              {aboutInfo.faqs.map((faq, i) => (
                <div
                  key={i}
                  className="rounded-xl bg-background-100 border border-background-200/70 overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-background-200/30 transition-colors"
                    onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                  >
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <i
                      className={`text-foreground-400 transition-transform duration-200 flex-shrink-0 ${
                        openFaqIndex === i ? 'ri-subtract-line' : 'ri-add-line'
                      }`}
                    ></i>
                  </button>
                  {openFaqIndex === i && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10 md:mb-14">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 font-heading">
              <i className="ri-mail-send-line text-primary-500 mr-2"></i>
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <h3 className="text-sm font-semibold text-foreground-900 mb-4 font-heading">联系方式</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400">邮箱</div>
                      <div className="text-foreground-800">{aboutInfo.contact.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 flex-shrink-0">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400">微信</div>
                      <div className="text-foreground-800">{aboutInfo.contact.wechat}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 flex-shrink-0">
                      <i className="ri-weibo-line"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400">微博</div>
                      <div className="text-foreground-800">{aboutInfo.contact.weibo}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 text-foreground-500 flex-shrink-0">
                      <i className="ri-time-line"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400">工作时间</div>
                      <div className="text-foreground-800">{aboutInfo.contact.workingHours}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <h3 className="text-sm font-semibold text-foreground-900 mb-4 font-heading">发送消息</h3>
                <ContactForm />
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}

function ContactForm() {
  const formUrl = 'https://readdy.ai/api/form/d9q9qome3oq4jqumtiog';
  const [success, setSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setSuccess(true);
      form.reset();
      setSubmitting(false);
      return;
    }

    const formData = new FormData(form);
    formData.delete('phone_alt');

    try {
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setSuccess(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后再试');
        }
      }
    } catch {
      setFormError('网络错误，请稍后再试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {success && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-accent-100 border border-accent-200/70 text-accent-800 text-sm">
          <i className="ri-checkbox-circle-line mr-2"></i>
          消息已发送！我们会尽快回复您。
        </div>
      )}
      {formError && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-primary-100 border border-primary-200/70 text-primary-800 text-sm">
          <i className="ri-error-warning-line mr-2"></i>
          {formError}
        </div>
      )}
      <form id="contact-form" data-readdy-form="true" onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          name="phone_alt"
          className="honeypot-field"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
        <input
          type="text"
          name="name"
          placeholder="您的姓名"
          required
          className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
        />
        <input
          type="email"
          name="email"
          placeholder="您的邮箱"
          required
          className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
        />
        <textarea
          name="message"
          placeholder="请输入您的留言..."
          required
          maxLength={500}
          rows={4}
          className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none"
        ></textarea>
        <button
          type="submit"
          disabled={submitting}
          className="w-full px-6 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
        >
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin mr-2"></i>发送中...
            </>
          ) : (
            <>
              <i className="ri-send-plane-line mr-2"></i>发送消息
            </>
          )}
        </button>
      </form>
    </>
  );
}